import { useEffect, useState } from 'react'

import { getGridRowBand } from './gridRowBand'

/**
 * How much of the row has to be showing to count as visible.
 *
 * Not 1: a fractional scroll offset or a non-integral device pixel ratio can
 * leave a row that is visually whole reporting a ratio a hair under it, and
 * flickering the overlay off at the top of a scroll is worse than tolerating
 * half a pixel of clipping.
 */
const FULLY_VISIBLE_RATIO = 0.99

/**
 * Whether the grid row an overlay is anchored to is wholly in view.
 *
 * Anything the action panel portals out of its cell — the action menu's
 * dropdown, the inline bid overlay — follows its anchor as the grid scrolls,
 * which is not on its own enough. Past the edge of the grid's viewport the
 * overlay is still glued to that anchor, floating over the page's header and
 * pointing at a row nobody can see. Callers hide it when this returns `false`.
 *
 * **Whole, not merely present.** These overlays stand taller than the row they
 * belong to and are centred on it, so a row that is only half in view puts its
 * overlay through the grid's header. Waiting for the row to leave entirely
 * leaves it hanging there the whole way out; requiring the row to be complete
 * takes it away the moment the row starts being cut off.
 *
 * **And visible means unobscured, not merely unclipped.** The header is pinned
 * inside the scrolling element rather than sitting above it, so a row scrolled
 * under the header is not clipped by anything — an intersection observer left
 * to itself calls it wholly visible while none of it can be seen, and the
 * overlay hangs over the column titles. The header's height comes off the top
 * of the root below to close that gap.
 *
 * Both of those are why this is about a row rather than any element, and why it
 * lives here rather than in a general helpers folder: the rule it encodes is
 * the grid's, not a property of elements in general.
 *
 * This is the bargain fe-core's `Tooltip` strikes with floating-ui's `hide`
 * middleware, which `Popover` does not forward — so the row's own visibility is
 * watched here instead.
 */
export const useIsRowVisible = (rowElement: HTMLElement | null) => {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    if (!rowElement) return

    /*
     * Outside a grid there is no band to measure against, so fall back to the
     * plain root: the overlay then hides a little later than it should rather
     * than not at all.
     */
    const band = getGridRowBand(rowElement)

    /*
     * The thresholds are what keep the answer current. Callbacks fire only when
     * a threshold is crossed, and the sample taken as the row crosses 1 can
     * still read as effectively whole — so the ratio either side of the cutoff
     * is listed too, and the row reports again a moment later once it is
     * unambiguously out.
     */
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1]
        if (entry) setIsVisible(entry.intersectionRatio >= FULLY_VISIBLE_RATIO)
      },
      {
        root: band?.scroller ?? null,
        rootMargin: `-${Math.ceil(band?.headerHeight ?? 0)}px 0px 0px 0px`,
        threshold: [0, FULLY_VISIBLE_RATIO, 1],
      }
    )
    observer.observe(rowElement)
    return () => observer.disconnect()
  }, [rowElement])

  return isVisible
}
